import { useCallback, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import { useKnowledgeBaseDocuments } from '@/hooks/useKnowledgeBaseDocuments';

export const useKnowledgeBaseDocumentOperations = (
  activeKnowledgeBaseId: string | null,
  fetchKnowledgeBaseDetails: () => Promise<void>,
  fetchKnowledgeBases?: () => Promise<void>
) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const documentStore = useKnowledgeBaseDocuments();

  const refresh = useCallback(async () => {
    await fetchKnowledgeBaseDetails();
    await fetchKnowledgeBases?.();
  }, [fetchKnowledgeBaseDetails, fetchKnowledgeBases]);

  const addDocument = useCallback(async (
    name: string,
    content: string,
    fileType: string,
    size: number,
    storagePath: string | null = null
  ) => {
    if (!user || !activeKnowledgeBaseId) {
      toast({ title: 'Error', description: 'User not authenticated or no knowledge base selected.', variant: 'destructive' });
      return null;
    }

    setLoading(true);
    try {
      const created = await documentStore.addDocument(activeKnowledgeBaseId, {
        name,
        content,
        fileType,
        size,
        storagePath,
      });
      if (!created) return null;
      await refresh();
      return created;
    } finally {
      setLoading(false);
    }
  }, [user, activeKnowledgeBaseId, documentStore, refresh]);

  const updateDocument = useCallback(async (
    documentId: string,
    updates: Partial<KnowledgeBaseDocument>
  ) => {
    if (!user || !activeKnowledgeBaseId || !updates.name || !updates.content) {
      toast({ title: 'Error', description: 'Document update is missing required fields.', variant: 'destructive' });
      return null;
    }

    setLoading(true);
    try {
      const updated = await documentStore.updateDocument(documentId, {
        name: updates.name,
        content: updates.content,
        fileType: updates.file_type ?? undefined,
        size: updates.size ?? undefined,
      });
      if (!updated) return null;
      await refresh();
      return updated;
    } finally {
      setLoading(false);
    }
  }, [user, activeKnowledgeBaseId, documentStore, refresh]);

  const deleteDocument = useCallback(async (documentId: string) => {
    if (!user || !activeKnowledgeBaseId) {
      toast({ title: 'Error', description: 'User not authenticated or no knowledge base selected.', variant: 'destructive' });
      return false;
    }

    setLoading(true);
    try {
      const deleted = await documentStore.deleteDocument(documentId);
      if (!deleted) return false;
      await refresh();
      return true;
    } finally {
      setLoading(false);
    }
  }, [user, activeKnowledgeBaseId, documentStore, refresh]);

  return {
    loading,
    addDocument,
    updateDocument,
    deleteDocument,
  };
};
